import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, fileUrl, formatApiError } from "../lib/api";
import { Skull, Loader2, ImageIcon, ArrowRight } from "lucide-react";
import { SkullRating } from "../components/app/SkullRating";

export default function RolesPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/roles/${slug}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(formatApiError(e)));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F2EBE1] flex items-center justify-center px-6">
        <div className="text-center" data-testid="roles-public-error">
          <Skull size={32} className="text-blood mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-display text-2xl uppercase tracking-tight">Roles Not Found</h1>
          <p className="text-sm text-[#A19D94] mt-2">This link is invalid or no longer available.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-blood" size={28} />
      </div>
    );
  }

  const roles = data.roles || [];

  return (
    <div className="min-h-screen bg-[#050505] text-[#F2EBE1] py-10 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Brand */}
        <div className="inline-flex items-center gap-3 mb-8">
          <Skull size={24} className="text-blood" strokeWidth={1.5} />
          <span className="font-display text-xl font-bold tracking-[0.04em] text-blood">SCARE AUDITIONS<span className="text-[0.45em] font-semibold tracking-normal align-top opacity-80 ml-1">.co.uk</span></span>
        </div>

        {/* Header */}
        <div className="border-b-2 border-blood pb-5 mb-8">
          <div className="label-mini text-blood">{data.event?.name}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight mt-1.5" data-testid="roles-public-heading">Meet the Characters</h1>
          <p className="text-sm text-[#A19D94] mt-3 max-w-2xl">Take a look at the roles available this season. You'll be asked about your character preferences on the day of your audition.</p>
        </div>

        {roles.length === 0 ? (
          <div className="border border-[#1a1a1a] bg-[#0A0A0A] py-16 text-center text-[#6B6862]" data-testid="roles-public-empty">
            No characters have been published yet. Check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="roles-public-grid">
            {roles.map((r) => (
              <div key={r.role_id} data-testid={`roles-public-card-${r.role_id}`} className="group border border-[#1a1a1a] bg-[#0A0A0A] overflow-hidden hover:border-blood/50 transition-colors">
                <div className="aspect-[3/4] bg-[#050505] overflow-hidden flex items-center justify-center">
                  {r.profile_image ? (
                    <img src={fileUrl(r.profile_image, { thumb: true })} alt={r.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 bg-[#1a1a1a]" />
                  ) : (
                    <ImageIcon size={28} className="text-[#3a3835]" />
                  )}
                </div>
                <div className="p-3">
                  <div className="font-display text-lg uppercase tracking-tight leading-none truncate" data-testid={`roles-public-name-${r.role_id}`}>{r.name}</div>
                  {r.character_name && <div className="text-[11px] text-[#A19D94] mt-1 truncate">{r.character_name}</div>}
                  {r.skill_level > 0 && <SkullRating value={r.skill_level} size={14} className="mt-2" />}
                  <Link to={`/${data.event?.slug}/character/${r.role_id}`} data-testid={`roles-public-view-${r.role_id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-blood hover:text-[#FF3333]">
                    View Character Sheet <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
